import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Lock, Mail, ShieldCheck } from 'lucide-react';
import * as C from '../data/content';
import * as I from '../data/images';
import { trackPixel } from '../utils/metaPixel';
import { CtaButton, GreenCheck, H2, Img, Kicker, Rich, Section, Sub } from './Ui';

/* Same order as the model page (clubedamateria.com.br/italianovisual):
   top bar · hero · see the material (carousel) · at home (photos) · where to use it · 20 chapters · why it works ·
   what you get today · 5 bonuses · is it for you · OFFER (only checkout button) · guarantee · faq · closing · footer.
   Not copied: the dated "only today" bar and the countdown, the testimonial, the prints and the reviews (we have none). */

export function TopBar() {
  return <div className="bg-band px-4 py-2.5 text-center text-[13px] font-semibold leading-snug text-white" data-id="element-18" data-mp="3fbkw8h6ou2i4">{C.topBar}</div>;
}

/** Hero: only title, short sub, the box mockup and the button (Luca's fixed rule) */
export function Hero() {
  return <header className="bg-cream text-ink" data-id="element-19" data-mp="17scjdvbsgo1x">
      <div className="mx-auto max-w-5xl px-5 pb-10 pt-7 text-center sm:px-8 sm:pb-14 sm:pt-12" data-id="element-20" data-mp="1cr5ph2oqxcs2">
        <p className="mx-auto max-w-md text-[13px] font-extrabold uppercase leading-snug tracking-[0.12em] text-verde" data-id="element-21" data-mp="y29k5de2qdv5">{C.hero.kicker}</p>
        <h1 className="mx-auto mt-3 max-w-3xl font-serif text-[31px] font-black leading-[1.12] tracking-[-0.01em] sm:text-[48px]" data-id="element-22" data-mp="12z9q7oaxbgm9">
          <Rich text={C.hero.title} data-id="element-23" data-mp="p3frtz7s09ne" />
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-[16.5px] leading-relaxed text-ink-soft" data-id="element-24" data-mp="t3pup309ujy1">{C.hero.sub}</p>
        <Img src={I.heroMockupUrl} alt={C.hero.mockupAlt} eager ratio={I.mockupRatio} className="mx-auto mt-4 w-[78%] max-w-[440px]" data-id="element-25" data-mp="f470zf87jb59" />
        <div className="mt-4" data-id="element-26" data-mp="k42vmnv0lq85">
          <CtaButton label={C.hero.cta} to="offer" data-id="element-27" data-mp="29h6d2yddpn7s" />
        </div>
      </div>
    </header>;
}

/** Horizontal carousel with arrows */
function Carousel({
  items,
  ratio,
  width = 'w-[88%] sm:w-[48%]',
  frame = true
}: {
  items: {
    url: string;
    alt: string;
  }[];
  ratio: string;
  width?: string;
  frame?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const move = (dir: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({
      left: dir * el.clientWidth * 0.85,
      behavior: 'smooth'
    });
  };
  return <div className="mt-7" data-id="element-28" data-mp="1y2mps71uu87w">
      <div ref={ref} className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 sm:mx-0 sm:px-0" data-id="element-29" data-mp="1y3ec0r9katli">
        {items.map(it => <div key={it.alt} className={`${width} shrink-0 snap-center`} data-id="element-30" data-mp="2qpz6w1p8xntt">
            <Img src={it.url} alt={it.alt} ratio={ratio} className={`w-full object-cover ${frame ? 'rounded-xl border border-ink/10 bg-white shadow-xl' : 'rounded-2xl shadow-lg'} ${ratio}`} data-id="element-31" data-mp="2lz8ysgpfr6vf" />
          </div>)}
      </div>
      <div className="mt-4 flex justify-center gap-3" data-id="element-32" data-mp="3egv2zb0qj56v">
        {[-1, 1].map(d => <button key={d} type="button" onClick={() => move(d as 1 | -1)} aria-label={d < 0 ? 'Zurück' : 'Weiter'} className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 bg-white text-ink shadow-sm" data-id="element-33" data-mp="39ouwt0u2ng1u">
            {d < 0 ? <ChevronLeft className="h-5 w-5" data-id="element-34" data-mp="3qxdwq5hrwpcw" /> : <ChevronRight className="h-5 w-5" data-id="element-35" data-mp="3r0icm04lp9je" />}
          </button>)}
      </div>
    </div>;
}
export function Material() {
  return <Section tone="cream2" data-id="element-36" data-mp="4s0l9703rkx3">
      <Kicker data-id="element-37" data-mp="4qphv8akkf6i">{C.material.kicker}</Kicker>
      <H2 text={C.material.title} data-id="element-38" data-mp="8ssvo3kpnvuy" />
      <Sub data-id="element-39" data-mp="8rr6ywarvaur">{C.material.sub}</Sub>
      <p className="mt-6 text-center text-[13px] font-bold uppercase tracking-[0.12em] text-ink-mute" data-id="element-40" data-mp="8qzo8m29x7lw">{C.material.swipe}</p>
      <Carousel items={I.materialPages} ratio="aspect-[297/210]" data-id="element-41" data-mp="dlvhtqn5s314" />
      <div className="mt-7" data-id="element-42" data-mp="ii437lry8sgq">
        <CtaButton label={C.material.cta} to="offer" data-id="element-43" data-mp="ikgjlui26uro" />
      </div>
    </Section>;
}
export function Zuhause() {
  const items = I.zuhausePhotos.map((url, i) => ({
    url,
    alt: C.zuhause.alts[i]
  }));
  return <Section tone="cream" data-id="element-44" data-mp="1p86owchmh7cc">
      <Kicker data-id="element-45" data-mp="1p7eyrh6so1tg">{C.zuhause.kicker}</Kicker>
      <H2 text={C.zuhause.title} data-id="element-46" data-mp="1p63s99lysfyn" />
      <Sub data-id="element-47" data-mp="1k9db0iyfbebn">{C.zuhause.sub}</Sub>
      <Carousel items={items} ratio="aspect-[3/4]" width="w-[78%] sm:w-[32%]" frame={false} data-id="element-48" data-mp="1k8278j4za6kj" />
      <p className="mt-3 text-center text-[14px] font-semibold text-ink-mute" data-id="element-49" data-mp="1ywygukmonrn9">{C.zuhause.swipe}</p>
    </Section>;
}
export function Wo() {
  return <Section tone="cream2" data-id="element-50" data-mp="1u19eyan6xyns">
      <Kicker data-id="element-51" data-mp="168swhyzjk4ny">{C.wo.kicker}</Kicker>
      <H2 text={C.wo.title} data-id="element-52" data-mp="169ufqqqpnxgr" />
      <Sub data-id="element-53" data-mp="16b5jiqk5p57v">{C.wo.sub}</Sub>
      <div className="mx-auto mt-7 grid max-w-3xl items-center gap-6 sm:grid-cols-2" data-id="element-54" data-mp="11arab56tjp89">
        <Img src={I.woPhoto} alt={C.wo.alt} ratio="aspect-[3/4]" className="w-full rounded-2xl object-cover shadow-lg" data-id="element-55" data-mp="1f9r1ajht0g7l" />
        <ul className="space-y-3" data-id="element-56" data-mp="1b85pz2kx49zi">
          {C.wo.punkte.map(p => <li key={p} className="rounded-xl bg-white px-4 py-3.5 text-[16px] font-semibold leading-snug shadow-sm" data-id="element-57" data-mp="2qow4dolukgrq">
              {p}
            </li>)}
        </ul>
      </div>
    </Section>;
}
export function Kapitel() {
  return <Section tone="cream" data-id="element-58" data-mp="2u6aaw8wrxu0w">
      <Kicker data-id="element-59" data-mp="2u969lzgg4mqe">{C.kapitelTitel.kicker}</Kicker>
      <H2 text={C.kapitelTitel.title} data-id="element-60" data-mp="2z6g9k91gogsh" />
      <Sub data-id="element-61" data-mp="2z7hncl9v3hdw">{C.kapitelTitel.sub}</Sub>
      <ol className="mx-auto mt-8 grid max-w-4xl gap-3 sm:grid-cols-2" data-id="element-62" data-mp="2k95r2fqeian9">
        {C.kapitel.map((k, i) => <li key={k.t} className="flex gap-3.5 rounded-2xl border border-ink/10 bg-cream-card p-4 shadow-sm" data-id="element-63" data-mp="2p7h0ipcsaj4y" data-mpi={i}>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-verde font-serif text-[17px] font-black text-white" data-id="element-64" data-mp="3cxbe594xgbq9">{String(i + 1).padStart(2, '0')}</span>
            <div data-id="element-65" data-mp="338bcfhz1j28d">
              <h3 className="font-serif text-[18px] font-extrabold leading-snug" data-id="element-66" data-mp="336qhhr8tdh9z">{k.t}</h3>
              <p className="mt-0.5 text-[14.5px] leading-relaxed text-ink-soft" data-id="element-67" data-mp="38630u20h867t">{k.d}</p>
            </div>
          </li>)}
      </ol>
    </Section>;
}
export function Warum() {
  return <Section tone="cream2" data-id="element-68" data-mp="4i1avxpax0gw">
      <Kicker data-id="element-69" data-mp="4jcfu0uhge9l">{C.warum.kicker}</Kicker>
      <H2 text={C.warum.title} data-id="element-70" data-mp="edrq6ovg1ver" />
      <div className="mx-auto mt-8 grid max-w-4xl gap-4 sm:grid-cols-2" data-id="element-71" data-mp="ecp2sqxbyb3l">
        {C.warum.karten.map(k => <article key={k.t} className="rounded-2xl bg-white p-5 shadow-sm" data-id="element-72" data-mp="9fymc2hm5dhn">
            <span className="text-[30px] leading-none" aria-hidden="true" data-id="element-73" data-mp="5cagppcjkrz1">
              {k.e}
            </span>
            <h3 className="mt-2.5 font-serif text-[20px] font-extrabold leading-snug" data-id="element-74" data-mp="cxkidzs68js">{k.t}</h3>
            <p className="mt-1.5 text-[15.5px] leading-relaxed text-ink-soft" data-id="element-75" data-mp="3rlbmhp7hemum">{k.d}</p>
          </article>)}
      </div>
    </Section>;
}
export function Bekommst() {
  return <Section tone="cream" data-id="element-76" data-mp="3htp67egadxl0">
      <Kicker data-id="element-77" data-mp="3hv06v5efqp7w">{C.bekommst.kicker}</Kicker>
      <H2 text={C.bekommst.title} data-id="element-78" data-mp="3dsvj2nao4mt6" />
      <Sub data-id="element-79" data-mp="3dtwtqqnrv7ad">{C.bekommst.sub}</Sub>
      <Img src={I.offerMockupUrl} alt={C.hero.mockupAlt} ratio={I.mockupRatio} className="mx-auto mt-6 w-full max-w-[360px]" data-id="element-80" data-mp="3duq21w2dq4um" />
      <ul className="mx-auto mt-6 max-w-2xl space-y-3" data-id="element-81" data-mp="33upetz1e34l5">
        {C.bekommst.punkte.map(p => <li key={p.t} className="flex items-start gap-3 rounded-2xl bg-cream-card p-4 shadow-sm" data-id="element-82" data-mp="1jcna42ro0rwr">
            <GreenCheck className="mt-0.5" data-id="element-83" data-mp="1obri4q2usjli" />
            <div className="flex-1" data-id="element-84" data-mp="1oagfitl59hvz">
              <p className="text-[16px] font-bold leading-snug" data-id="element-85" data-mp="1odkr4c0xvfwo">{p.t}</p>
              <p className="mt-0.5 text-[14.5px] leading-relaxed text-ink-soft" data-id="element-86" data-mp="1tbchv2pukvza">{p.d}</p>
            </div>
            <span className="shrink-0 rounded-full bg-verde-light px-2.5 py-1 text-[11.5px] font-extrabold uppercase tracking-wide text-verde" data-id="element-87" data-mp="1xbmzjcn05atz">{C.bekommst.inklusive}</span>
          </li>)}
      </ul>
    </Section>;
}
export function Boni() {
  return <Section tone="cream2" data-id="element-88" data-mp="19mljp4buaxeo">
      <Kicker data-id="element-89" data-mp="19lalrl52sjvh">{C.boni.kicker}</Kicker>
      <H2 text={C.boni.title} data-id="element-90" data-mp="1edk5gjflknpb" />
      <Sub data-id="element-91" data-mp="1edtzqj7odh0t">{C.boni.sub}</Sub>
      <div className="mx-auto mt-8 grid max-w-4xl gap-5 sm:grid-cols-2" data-id="element-92" data-mp="1ef52cfpdwiqc">
        {C.boni.liste.map((b, i) => <article key={b.t} className="overflow-hidden rounded-2xl bg-white shadow-sm" data-id="element-93" data-mp="htl61un6g9vc" data-mpi={i}>
            <Img src={I.bonusCovers[i]} alt={b.t} ratio="aspect-[297/210]" className="w-full bg-cream object-cover" data-id="element-94" data-mp="lvg9b4oo4xbr" />
            <div className="p-5" data-id="element-95" data-mp="3bh5wqtwn4hny">
              <p className="text-[12.5px] font-extrabold uppercase tracking-[0.14em] text-rosso" data-id="element-96" data-mp="36h1fv2rtlkx7">Bonus {i + 1}</p>
              <h3 className="mt-1 font-serif text-[20px] font-extrabold leading-snug" data-id="element-97" data-mp="3lek0bsxgtbyu">{b.t}</h3>
              <p className="mt-1.5 text-[15px] leading-relaxed text-ink-soft" data-id="element-98" data-mp="3gde77tzw7mos">{b.d}</p>
              <p className="mt-3 text-[15px] font-bold" data-id="element-99" data-mp="3udf8hak2kw4s">
                <s className="text-ink-mute" data-id="element-100" data-mp="3uc47tjlx84hw">{C.pricing.bonusValue}</s> <span className="ml-1 text-verde" data-id="element-101" data-mp="3pe2oqyohw56f">{C.boni.gratis}</span>
              </p>
            </div>
          </article>)}
      </div>
      <p className="mt-7 text-center text-[16px] font-semibold text-ink-soft" data-id="element-102" data-mp="80gq77x257ya">{C.boni.fazit}</p>
      <div className="mt-5" data-id="element-103" data-mp="31m682nctdcl">
        <CtaButton label={C.boni.cta} to="offer" data-id="element-104" data-mp="gysozx82vc1t" />
      </div>
    </Section>;
}
export function FuerDich() {
  return <Section tone="cream" data-id="element-105" data-mp="cyqjte34w0hs">
      <Kicker data-id="element-106" data-mp="czjwevorxk7u">{C.fuerDich.kicker}</Kicker>
      <H2 text={C.fuerDich.title} data-id="element-107" data-mp="vm996eue22jp" />
      <div className="mx-auto mt-8 grid max-w-4xl gap-4 sm:grid-cols-2" data-id="element-108" data-mp="vnavjfguubgq">
        {C.fuerDich.liste.map(p => <article key={p.t} className="flex gap-3.5 rounded-2xl border border-ink/10 bg-cream-card p-5 shadow-sm" data-id="element-109" data-mp="10kkjeqolif2g">
            <span className="text-[28px] leading-none" aria-hidden="true" data-id="element-110" data-mp="mnfe4swphk89">
              {p.e}
            </span>
            <div data-id="element-111" data-mp="cvi0zgm50i6w">
              <h3 className="font-serif text-[19px] font-extrabold leading-snug" data-id="element-112" data-mp="cuzxbyx5b1oy">… {p.t}</h3>
              <p className="mt-1 text-[15px] leading-relaxed text-ink-soft" data-id="element-113" data-mp="hlyh58hcehwy">{p.d}</p>
            </div>
          </article>)}
      </div>
    </Section>;
}
export function Offer() {
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let fired = false;
    const obs = new IntersectionObserver(entries => {
      if (!fired && entries.some(e => e.isIntersecting)) {
        fired = true;
        trackPixel('ViewContent', {
          value: C.pricing.value,
          currency: C.pricing.currency
        });
        obs.disconnect();
      }
    }, {
      threshold: 0.25
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <Section tone="dark" id="angebot" data-id="element-114" data-mp="2qen77vhf54ig">
      <Kicker light data-id="element-115" data-mp="2qfpy43eqr6yd">{C.offer.kicker}</Kicker>
      <H2 text={C.offer.title} light data-id="element-116" data-mp="2qgrcagr209kb" />
      <Sub light data-id="element-117" data-mp="3e3ppn5jvn1e0">{C.offer.sub}</Sub>
      <div id="offer-card" ref={cardRef} className="mx-auto mt-8 max-w-[480px] scroll-mt-4 overflow-hidden rounded-[26px] border-[3px] border-verde bg-white text-ink shadow-[0_24px_64px_rgba(0,0,0,0.45)]" data-id="element-118" data-mp="3e4r9nzivey7v">
        <div className="btn-verde px-5 py-2.5 text-center text-[14px] font-extrabold tracking-[0.14em] text-white" data-id="element-119" data-mp="39dizh19pv8tn">
          {C.offer.card} · {C.offer.cardSub}
        </div>
        <div className="p-5 sm:p-7" data-id="element-120" data-mp="vmjzcw98tvq">
          <Img src={I.offerMockupUrl} alt={C.hero.mockupAlt} ratio={I.mockupRatio} className="mx-auto w-full max-w-[340px]" data-id="element-121" data-mp="5o60oiq8o54r" />
          <ul className="mt-4 space-y-3" data-id="element-122" data-mp="1qdt9hbdx6ng5">
            {C.offer.main.map((t, i) => <li key={t} className="flex items-start gap-3 text-[16px] font-bold leading-snug" data-id="element-123" data-mp="1lgjk1vqj6bsb" data-mpi={i}>
                <GreenCheck className="mt-0.5" data-id="element-124" data-mp="1z7elimyp9qlv" />
                <span className="flex-1" data-id="element-125" data-mp="1z99783hspmr0">{t}</span>
                {i === 0 && <span className="shrink-0 text-[14.5px] font-semibold text-ink-mute" data-id="element-126" data-mp="1v6md1vxnjpqn">{C.pricing.mainValue}</span>}
              </li>)}
            {C.boni.liste.map(b => <li key={b.t} className="flex items-start gap-3 text-[15px] leading-snug" data-id="element-127" data-mp="12hntcudvsfj8">
                <span className="w-6 shrink-0 text-center text-[17px] leading-none" aria-hidden="true" data-id="element-128" data-mp="1gh6pspxby2fp">
                  🎁
                </span>
                <span className="flex-1" data-id="element-129" data-mp="1bg980eg8faeq">{b.t}</span>
                <span className="shrink-0 text-[14px] font-semibold" data-id="element-130" data-mp="2rvaouairygrm">
                  <s className="text-ink-mute" data-id="element-131" data-mp="2mwr58grnlh2f">{C.pricing.bonusValue}</s> <span className="text-verde" data-id="element-132" data-mp="2mvxul4pkq7f0">{C.boni.gratis}</span>
                </span>
              </li>)}
          </ul>
          <div className="mt-5 flex items-center justify-between border-t border-dashed border-ink/25 pt-4 text-[16.5px] font-bold" data-id="element-133" data-mp="1klnrluduxp0m">
            <span data-id="element-134" data-mp="1tjrfgxcbexvl">{C.pricing.totalLabel}</span>
            <s className="text-ink-mute" data-id="element-135" data-mp="1tksufcwgc4il">{C.pricing.totalValue}</s>
          </div>
          <div className="mt-4 text-center" data-id="element-136" data-mp="1yikhntm5j2gg">
            <p className="text-[14px] font-bold uppercase tracking-[0.14em] text-ink-mute" data-id="element-137" data-mp="11stl7ouav580">{C.pricing.todayLabel}</p>
            <p className="mt-1 font-serif text-[56px] font-black leading-none text-ink" data-id="element-138" data-mp="15tx4klbk3jdz">{C.pricing.price}</p>
            <p className="mx-auto mt-3 inline-block rounded-full bg-rosso-light px-4 py-1.5 text-[14.5px] font-extrabold text-rosso" data-id="element-139" data-mp="1at9n4tvea4ar">{C.pricing.savings}</p>
            <p className="mt-2.5 text-[13.5px] text-ink-mute" data-id="element-140" data-mp="1fm2vdrq2nurl">{C.pricing.note}</p>
          </div>
          <div className="mt-5" data-id="element-141" data-mp="i301zz5nhyfe">
            <CtaButton label={C.offer.cta} to="checkout" data-id="element-142" data-mp="n3fbgfmtals3" />
          </div>
          <p className="mt-2 text-center text-[12.5px] text-ink-mute" data-id="element-143" data-mp="fkehakxwi4he">{C.offer.ctaSub}</p>
          <ul className="mt-4 grid grid-cols-3 gap-2 text-center text-[12px] font-semibold leading-tight text-ink-soft" data-id="element-144" data-mp="fi238iv9ke9m">
            {C.trust.map((t, i) => {
            const Icon = [ShieldCheck, Lock, Mail][i];
            return <li key={t} className="flex flex-col items-center gap-1.5" data-id="element-145" data-mp="1les9c6h7nkp" data-mpi={i}>
                  <Icon className="h-5 w-5 text-verde" strokeWidth={2.25} aria-hidden="true" data-id="element-146" data-mp="3sqod6pfm5bng" />
                  <span data-id="element-147" data-mp="3o0hq4u0r1z7b">{t}</span>
                </li>;
          })}
          </ul>
        </div>
      </div>
    </Section>;
}
export function Garantie() {
  return <Section tone="cream" data-id="element-148" data-mp="3iyscd6m577fj">
      <div className="mx-auto max-w-xl rounded-[26px] border border-verde/30 bg-white p-6 text-center shadow-sm sm:p-9" data-id="element-149" data-mp="3e2k2a7msy4f8">
        <span className="mx-auto flex h-20 w-20 flex-col items-center justify-center rounded-full border-4 border-verde bg-verde-light font-serif text-[30px] font-black leading-none text-verde" data-id="element-150" data-mp="39zf2icu3wgua">
          10
          <span className="mt-0.5 font-sans text-[10px] font-extrabold uppercase tracking-wider" data-id="element-151" data-mp="18zigc9qmfv28">Tage</span>
        </span>
        <p className="mt-4 text-[13px] font-extrabold uppercase tracking-[0.14em] text-verde" data-id="element-152" data-mp="1cz15xite0ube">{C.garantie.kicker}</p>
        <h2 className="mt-1 font-serif text-[27px] font-black leading-tight" data-id="element-153" data-mp="z6l58onyl8dn">{C.garantie.title}</h2>
        {C.garantie.text.map(t => <p key={t} className="mt-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-154" data-mp="p9yl623gilco">
            {t}
          </p>)}
      </div>
    </Section>;
}
export function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  return <Section tone="cream2" id="faq" data-id="element-155" data-mp="gbmobibzyrbs">
      <H2 text={C.faq.title} data-id="element-156" data-mp="l8d6qcb9xz0d" />
      <div className="mx-auto mt-7 max-w-2xl divide-y divide-ink/10 rounded-2xl border border-ink/10 bg-cream-card" data-id="element-157" data-mp="l9ftc81kdfah">
        {C.faq.liste.map((it, i) => {
        const isOpen = open === i;
        return <div key={it.q} data-id="element-158" data-mp="2fm5qcyn8b4m3" data-mpi={i}>
              <h3 data-id="element-159" data-mp="2fkbahyqte2ov">
                <button type="button" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : i)} className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left text-[16px] font-bold leading-snug" data-id="element-160" data-mp="2pq2vav9h6xld">
                  <span data-id="element-161" data-mp="2trxv1vbrd2x1">{it.q}</span>
                  <ChevronDown className={`h-5 w-5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-162" data-mp="2tqmojnqxhh28" />
                </button>
              </h3>
              {isOpen && <p className="px-5 pb-5 text-[15px] leading-relaxed text-ink-soft" data-id="element-163" data-mp="33h7nxdefepm7">{it.a}</p>}
            </div>;
      })}
      </div>
    </Section>;
}
export function Schluss() {
  return <Section tone="dark" data-id="element-164" data-mp="3dhhysrcznky7">
      <h2 className="mx-auto max-w-3xl text-center font-serif text-[29px] font-black leading-[1.16] sm:text-[40px]" data-id="element-165" data-mp="3de2i5k3o23uo">
        <Rich text={C.schluss.title} light data-id="element-166" data-mp="1j25qbq1bavz3" />
      </h2>
      <div className="mt-7" data-id="element-167" data-mp="1j3qjpcbw48vd">
        <CtaButton label={C.schluss.cta} to="offer" data-id="element-168" data-mp="1j4ifan5hm6ln" />
      </div>
      <p className="mt-3 text-center text-[13.5px] text-white/70" data-id="element-169" data-mp="3i87c2kec58yt">{C.schluss.note}</p>
    </Section>;
}
export function Footer() {
  return <footer className="border-t border-ink/10 bg-cream px-5 py-8 text-center text-[12px] leading-relaxed text-ink-mute" data-id="element-170" data-mp="3s53fibpdn4wi">
      <p className="mx-auto max-w-2xl font-semibold" data-id="element-171" data-mp="zbs3k2zi6de">{C.footer.line}</p>
      <p className="mx-auto mt-2 max-w-2xl" data-id="element-172" data-mp="50drq20tckfw">{C.footer.disclaimer}</p>
    </footer>;
}